import { useState } from 'react';

import { TabShape } from '@/components/ui/TabShape';
import { cn } from '@/lib/utils';

import {
  TAB_H,
  EAR,
  TOP_RADIUS,
  BOTTOM_RAMP,
  STROKE_W,
  MERGE_OFFSET,
  TAB_RAMP_DY,
  SEAM,
  NEUTRAL_700,
} from './constants';

// Documents / Marketplace.
//  • ACTIVE  → a normal merged tab (UNCHANGED): bottom-aligned, merges into the
//    seam (fill = its page color), title centered in the tab.
//  • HOVER (inactive) → a grey "ghost tab": full tab height down to the toolbar
//    border, grey fill, no border, only on hover. Label stays centered in the bar.
//  • RESTING → just the centered menu-item label.
export function FixedTab({
  label,
  width,
  active,
  onClick,
  pageFill,
}: {
  label: string;
  width: number;
  active: boolean;
  onClick?: () => void;
  /** bg of the page this tab opens — the ACTIVE merged tab fills with it so it
   *  dissolves into that page (the route nulls the toolbar). */
  pageFill: string;
}) {
  const [hover, setHover] = useState(false);

  // ACTIVE — original merged tab, untouched.
  if (active) {
    return (
      <div
        className="relative self-end"
        style={{ height: TAB_H }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div
          className="absolute inset-x-0 top-0"
          style={{ top: -MERGE_OFFSET }}
        >
          <TabShape
            variant="trapezoid"
            width={width}
            height={TAB_H}
            ear={EAR}
            radius={TOP_RADIUS}
            bottomRadius={BOTTOM_RAMP}
            strokeWidth={STROKE_W}
            fill={pageFill}
            stroke={SEAM}
            openBottom
            rampDy={TAB_RAMP_DY}
          />
        </div>
        <button
          onClick={onClick}
          style={{ width }}
          className="relative flex h-full items-center justify-center text-xs text-[#107d40]"
        >
          {label}
        </button>
      </div>
    );
  }

  // INACTIVE — centered menu item; HOVER paints a grey ghost tab behind it that
  // runs full tab height down to the toolbar border (positioned exactly like a
  // real tab: bottom dropped MERGE_OFFSET onto the seam).
  return (
    <div
      className="relative self-stretch"
      style={{ width }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {hover && (
        <div
          className="pointer-events-none absolute inset-x-0"
          style={{ height: TAB_H, bottom: MERGE_OFFSET }}
        >
          <TabShape
            variant="trapezoid"
            width={width}
            height={TAB_H}
            ear={EAR}
            radius={TOP_RADIUS}
            bottomRadius={BOTTOM_RAMP}
            fill={NEUTRAL_700}
            rampDy={TAB_RAMP_DY}
          />
        </div>
      )}
      <button
        onClick={onClick}
        className={cn(
          'relative z-10 flex h-full w-full items-center justify-center text-xs transition-colors',
          hover ? 'text-[#107d40]' : 'text-telekinesis-text-secondary',
        )}
      >
        {label}
      </button>
    </div>
  );
}
